import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { api, fmtDate, fmtNum, curSym, errMsg } from "@finance/lib/api";
import { exportToCsv } from "@finance/lib/exportCsv";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { DateField } from "@finance/components/DateField";
import { Label } from "@finance/components/ui/label";
import { Textarea } from "@finance/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { SearchableSelect } from "@finance/components/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@finance/components/ui/tabs";
import { QuickAddButton } from "@finance/components/QuickAdd";
import { DocumentPreview } from "@finance/components/DocumentPreview";
import { CurrencySelect } from "@finance/components/CurrencySelect";
import { toast } from "sonner";
import { Plus, Trash, Scan, CircleNotch, Receipt, Coins, Package, ArrowRight, ArrowLeft, MagnifyingGlass, DownloadSimple } from "@phosphor-icons/react";
import { MultiSelectFilter } from "@finance/components/MultiSelectFilter";
import { KpiStrip, KpiCard, EmptyState, TableEmpty, SectionCard } from "@finance/components/DesignSystem";
import { DocNumberField } from "@finance/components/DocNumberField";
import { DeliverInvoice } from "@finance/components/DeliverInvoice";
import { RequestAdvanceButton } from "@finance/components/AdvanceApply";

const blankLine = () => ({ item_id: "", sku: "", description: "", qty: "", unit_price: "", tax_type: "Vatable", line_type: "inventory" });

const STATUS_TONE = {
  "Not Started": "badge-neutral",
  "Partially Fulfilled": "badge-warning",
  "Partially Invoiced": "badge-info",
  "Delivered": "badge-info",
  "Completed": "badge-success",
  "Complete": "badge-success",
};
const StatusPill = ({ status }) => (
  <span className={`badge ${STATUS_TONE[status] || "badge-neutral"}`}><span className="badge-dot" />{status || "Not Started"}</span>
);

export default function ClientPOs() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: routeId } = useParams();
  const [rows, setRows] = useState([]);
  const CPO_STATUSES = ["Not Started", "Partially Fulfilled", "Delivered", "Partially Invoiced", "Completed"];
  const [statusSel, setStatusSel] = useState(() => { try { return JSON.parse(localStorage.getItem("lx.cpo.statusSel")) || []; } catch { return []; } });
  useEffect(() => { localStorage.setItem("lx.cpo.statusSel", JSON.stringify(statusSel)); }, [statusSel]);
  const [clients, setClients] = useState([]);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ currency: "TZS", fx_rate: 1, rfq_no: "", line_items: [blankLine()] });
  const [ocrLoading, setOcrLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [ful, setFul] = useState({});
  const [fulfilling, setFulfilling] = useState(false);

  const load = useCallback(() => api.get("/client-pos").then((r) => setRows(r.data)), []);
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/items").then((r) => setItems(r.data)).catch(() => {});
  }, [load]);

  const openNew = () => navigate("/client-pos/new");
  const openEdit = (p) => setForm({ ...p, fx_rate: p.fx_rate || 1, line_items: (p.line_items && p.line_items.length ? p.line_items.map((l) => ({ ...l })) : [blankLine()]) }) || setOpen(true);

  const setLine = (i, patch) => setForm((s) => {
    const li = [...(s.line_items || [])];
    li[i] = { ...li[i], ...patch };
    return { ...s, line_items: li };
  });
  const pickItem = (i, itemId) => {
    const it = items.find((x) => x.id === itemId);
    setLine(i, { item_id: itemId, sku: it?.sku || "", description: it?.name || it?.description || "" });
  };
  const addLine = () => setForm((s) => ({ ...s, line_items: [...(s.line_items || []), blankLine()] }));
  const addOtherLine = () => setForm((s) => ({ ...s, line_items: [...(s.line_items || []), { ...blankLine(), line_type: "other" }] }));
  const removeLine = (i) => setForm((s) => ({ ...s, line_items: s.line_items.filter((_, j) => j !== i) }));

  const [poPreview, setPoPreview] = useState(false);
  const [q, setQ] = useState("");
  const poTotal = (form.line_items || []).reduce((sum, l) => sum + (Number(l.qty || 0) * Number(l.unit_price || 0)), 0);
  const tzsVal = (p) => (p.line_items || []).reduce((s, l) => s + (l.exclusive_amount || 0), 0) * (p.fx_rate || 1);
  const filtered = rows.filter((p) => {
    if (statusSel.length && !statusSel.includes(p.fulfillment_status || "Not Started")) return false;
    if (q) {
      const hay = `${p.client_po_no || ""} ${p.client_name || ""} ${p.rfq_no || ""} ${(p.lynx_po_nos || []).join(" ")}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  });
  const cpoOpen = filtered.filter((p) => (p.fulfillment_status || "Not Started") !== "Completed");
  const cpoOpenValue = cpoOpen.reduce((s, p) => s + tzsVal(p), 0);
  const cpoAwaitFul = filtered.filter((p) => ["Not Started", "Partially Fulfilled"].includes(p.fulfillment_status || "Not Started"));
  const cpoAwaitFulValue = cpoAwaitFul.reduce((s, p) => s + tzsVal(p), 0);
  const cpoAwaitInv = filtered.filter((p) => ["Delivered", "Partially Invoiced"].includes(p.fulfillment_status));
  const cpoAwaitInvValue = cpoAwaitInv.reduce((s, p) => s + tzsVal(p), 0);

  const runOcr = async (file) => {
    if (!file) return;
    setOcrLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("document_type", "Purchase_Order");
    try {
      const r = await api.post("/ocr/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const f = r.data?.fields || {};
      const patch = {};
      if (f.currency && ["TZS", "USD"].includes(String(f.currency).toUpperCase())) patch.currency = String(f.currency).toUpperCase();
      if (f.client_po_no) patch.client_po_no = String(f.client_po_no);
      if (f.po_date) patch.po_date = String(f.po_date);
      const ocrLines = f.line_items || [];
      if (ocrLines.length) {
        patch.line_items = ocrLines.map((l) => {
          const desc = (l.description || "").toLowerCase();
          const m = items.find((it) => (it.sku && desc.includes(it.sku.toLowerCase())) || (it.name && desc.includes(it.name.toLowerCase())));
          return {
            item_id: m?.id || "", sku: m?.sku || "", description: l.description || "",
            qty: l.qty ?? "", unit_price: l.unit_price ?? "", tax_type: "Vatable",
          };
        });
      }
      setForm((s) => ({ ...s, ...patch }));
      toast.success("PO scanned — review the extracted lines before saving");
    } catch (e) {
      toast.error(errMsg(e) || "Could not read the document");
    } finally {
      setOcrLoading(false);
    }
  };

  const save = async () => {
    if (!form.client_id || !form.rfq_no) { toast.error("Client and RFQ number are required"); return false; }
    const line_items = (form.line_items || [])
      .filter((l) => (l.description || l.sku) && Number(l.qty || 0) > 0)
      .map((l) => ({
        item_id: (l.line_type === "other" ? null : (l.item_id || null)), sku: (l.line_type === "other" ? null : (l.sku || null)), description: l.description || "",
        qty: Number(l.qty || 0), unit_price: Number(l.unit_price || 0),
        exclusive_amount: Number((Number(l.qty || 0) * Number(l.unit_price || 0)).toFixed(2)),
        tax_type: l.tax_type || "Vatable", line_type: l.line_type || "inventory",
      }));
    const payload = {
      client_id: form.client_id, client_po_no: form.client_po_no || null, rfq_no: form.rfq_no, po_date: form.po_date || null,
      currency: form.currency || "TZS", fx_rate: Number(form.fx_rate || 1), notes: form.notes || null, line_items,
    };
    try {
      if (form.id) await api.put(`/client-pos/${form.id}`, payload);
      else await api.post("/client-pos", payload);
      toast.success("Client PO saved"); setOpen(false); load();
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete client PO ${p.client_po_no}?`)) return;
    await api.delete(`/client-pos/${p.id}`); toast.success("Deleted"); load();
  };

  const openDetail = async (cid) => {
    setDetail(null); setFul({}); setNotFound(false);
    try {
      const { data } = await api.get(`/client-pos/${cid}`);
      setDetail(data);
      const init = {};
      (data.lines || []).forEach((l) => { init[l.line_index] = { qty: "", unit_price: l.unit_price }; });
      setFul(init);
    } catch (e) { setNotFound(true); toast.error(errMsg(e)); }
  };
  const setFulLine = (idx, patch) => setFul((s) => ({ ...s, [idx]: { ...s[idx], ...patch } }));

  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId) { navigate(`/client-pos/${openId}`, { replace: true }); return; }
    if (routeId === "new") { setForm({ currency: "TZS", fx_rate: 1, rfq_no: "", line_items: [blankLine()] }); setDetail(null); return; }
    if (routeId) openDetail(routeId); else setDetail(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId, searchParams]);

  const doFulfill = async () => {
    if (!detail) return;
    const lines = (detail.lines || [])
      .map((l) => ({ line_index: l.line_index, qty: Number(ful[l.line_index]?.qty || 0), unit_price: Number(ful[l.line_index]?.unit_price ?? l.unit_price) }))
      .filter((l) => l.qty > 0);
    if (!lines.length) { toast.error("Enter a quantity to fulfil on at least one line"); return; }
    setFulfilling(true);
    try {
      const { data } = await api.post(`/client-pos/${detail.po.id}/fulfill`, { lines });
      toast.success(`Fulfillment ${data.lx_po_no} created — routing to Fulfillment`);
      load();
      navigate(`/transactions/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setFulfilling(false); }
  };

  const addToOpenPo = async () => {
    if (!detail) return;
    const lines = (detail.lines || [])
      .map((l) => ({ line_index: l.line_index, qty: Number(ful[l.line_index]?.qty || 0), unit_price: Number(ful[l.line_index]?.unit_price ?? l.unit_price) }))
      .filter((l) => l.qty > 0);
    if (!lines.length) { toast.error("Enter a quantity on at least one line"); return; }
    try {
      const open = (await api.get(`/client-pos/${detail.po.id}/open-supplier-pos`)).data || [];
      if (!open.length) { toast.error("No open supplier POs (without GRV, invoice or GL) to add to."); return; }
      const pick = window.prompt(`Add to which open PO?\n${open.map((t) => `${t.lx_po_no} · ${t.supplier_name || ""}`).join("\n")}\n\nType the PO number:`);
      const t = open.find((x) => x.lx_po_no === (pick || "").trim());
      if (!t) { if (pick) toast.error("PO not in the open list"); return; }
      await api.post(`/client-pos/${detail.po.id}/add-to-po`, { transaction_id: t.id, lines });
      toast.success(`Lines added to ${t.lx_po_no}`); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const cpoFormInner = (
    <>
          <div className="flex items-center justify-between rounded-sm border border-dashed border-[#1B4383]/40 bg-[#E6ECF5]/40 px-3 py-2">
            <span className="text-sm text-gray-600">Have the client's PO document? Scan it to auto-fill the lines below.</span>
            <label className={`inline-flex cursor-pointer items-center gap-1.5 rounded-sm border border-[#1B4383] bg-white px-2.5 py-1 text-xs font-semibold text-[#1B4383] transition-colors hover:bg-[#1B4383] hover:text-white ${ocrLoading ? "pointer-events-none opacity-60" : ""}`} data-testid="cpo-ocr-upload">
              {ocrLoading ? <CircleNotch size={14} className="animate-spin" /> : <Scan size={14} />}
              {ocrLoading ? "Scanning…" : "Scan PO (OCR)"}
              <input type="file" className="hidden" accept="image/*,application/pdf" disabled={ocrLoading}
                onChange={(e) => { runOcr(e.target.files[0]); e.target.value = ""; }} />
            </label>
          </div>

          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-2">
              <Label className="lx-label">Client</Label>
              <div className="mt-1 flex items-center gap-2">
                <SearchableSelect
                  testid="cpo-client-select"
                  placeholder="Select client"
                  value={form.client_id || ""}
                  onValueChange={(v) => setForm({ ...form, client_id: v })}
                  options={clients.map((c) => ({ value: c.id, label: c.name }))}
                />
                <QuickAddButton type="client" onCreated={(c) => { setClients((s) => [...s, c]); setForm((s) => ({ ...s, client_id: c.id })); }} />
              </div>
            </div>
            <DocNumberField docKey="client_po" label="Client PO No" testid="cpo-number" value={form.client_po_no} onChange={(v) => setForm({ ...form, client_po_no: v })} />
            <div><Label className="lx-label">RFQ No *</Label><Input className="mt-1" data-testid="cpo-rfq" value={form.rfq_no || ""} onChange={(e) => setForm({ ...form, rfq_no: e.target.value })} /></div>
            <div><Label className="lx-label">PO Date</Label><DateField className="mt-1" data-testid="cpo-date" value={(form.po_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, po_date: e.target.value })} /></div>
            <div>
              <Label className="lx-label">Currency</Label>
              <CurrencySelect className="mt-1" testid="cpo-currency" value={form.currency || "TZS"} onValueChange={(v) => setForm({ ...form, currency: v, fx_rate: v === "TZS" ? 1 : (form.fx_rate || 1) })} />
            </div>
            <div>
              <Label className="lx-label">FX Rate → TZS</Label>
              <Input type="number" className="mt-1" data-testid="cpo-fx-rate" disabled={(form.currency || "TZS") === "TZS"} value={form.fx_rate ?? 1} onChange={(e) => setForm({ ...form, fx_rate: e.target.value })} />
            </div>
          </div>

          <div className="mt-2">
            <div className="mb-1 flex items-center justify-between">
              <Label className="lx-label">Line Items</Label>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" data-testid="cpo-add-line" onClick={addLine}><Plus size={14} className="mr-1" />Add item</Button>
                <Button size="sm" variant="outline" data-testid="cpo-add-other-line" onClick={addOtherLine}><Plus size={14} className="mr-1" />Add charge</Button>
              </div>
            </div>
            <div className="overflow-hidden rounded-sm border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200 text-gray-400">
                  <th className="px-2 py-1.5 text-left">Inventory Item / Charge</th>
                  <th className="px-2 py-1.5 text-left">Description</th>
                  <th className="px-2 py-1.5 text-right">Units</th>
                  <th className="px-2 py-1.5 text-right">Selling / Unit</th>
                  <th className="px-2 py-1.5 text-left">Tax</th>
                  <th className="px-2 py-1.5 text-right">Total</th>
                  <th className="px-2 py-1.5"></th>
                </tr></thead>
                <tbody>
                  {(form.line_items || []).map((l, i) => (
                    <tr key={i} className="border-b border-gray-50" data-testid={`cpo-line-${i}`}>
                      <td className="px-2 py-1.5" style={{ minWidth: 210 }}>
                        {l.line_type === "other" ? (
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center rounded-sm bg-amber-100 px-2 py-1 text-[11px] font-semibold text-amber-700" data-testid={`cpo-line-other-badge-${i}`}>Other charge</span>
                            <button className="text-[11px] text-gray-400 hover:text-[#1B4383]" data-testid={`cpo-line-toinv-${i}`} onClick={() => setLine(i, { line_type: "inventory" })}>Use item</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <SearchableSelect
                              testid={`cpo-line-item-${i}`}
                              className="h-8"
                              placeholder="Select"
                              value={l.item_id || ""}
                              onValueChange={(v) => pickItem(i, v)}
                              options={items.map((it) => ({ value: it.id, label: `${it.sku} · ${it.name}` }))}
                            />
                            <QuickAddButton type="item" onCreated={(it) => { setItems((s) => [...s, it]); setLine(i, { item_id: it.id, sku: it.sku || "", description: it.name || "" }); }} />
                            <button className="whitespace-nowrap text-[11px] text-gray-400 hover:text-[#1B4383]" data-testid={`cpo-line-toother-${i}`} onClick={() => setLine(i, { line_type: "other", item_id: "", sku: "" })}>Other</button>
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1.5"><Input className="h-8" data-testid={`cpo-line-desc-${i}`} placeholder={l.line_type === "other" ? "e.g. Packing, Transport, Handling" : ""} value={l.description || ""} onChange={(e) => setLine(i, { description: e.target.value })} /></td>
                      <td className="px-2 py-1.5" style={{ width: 90 }}><Input type="number" className="h-8 text-right" data-testid={`cpo-line-qty-${i}`} value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} /></td>
                      <td className="px-2 py-1.5" style={{ width: 120 }}><Input type="number" className="h-8 text-right" data-testid={`cpo-line-price-${i}`} value={l.unit_price} onChange={(e) => setLine(i, { unit_price: e.target.value })} /></td>
                      <td className="px-2 py-1.5" style={{ width: 120 }}>
                        <Select value={l.tax_type || "Vatable"} onValueChange={(v) => setLine(i, { tax_type: v })}>
                          <SelectTrigger className="h-8" data-testid={`cpo-line-tax-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{["Vatable", "Zero-Rated", "Exempt", "Not VATable"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="px-2 py-1.5 text-right lx-num" style={{ width: 110 }}>{fmtNum(Number(l.qty || 0) * Number(l.unit_price || 0))}</td>
                      <td className="px-2 py-1.5 text-right"><button className="text-gray-300 hover:text-red-500" data-testid={`cpo-line-remove-${i}`} onClick={() => removeLine(i)}><Trash size={14} /></button></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr className="bg-gray-50 font-semibold"><td className="px-2 py-1.5" colSpan={5}>Total Selling Price ({form.currency || "TZS"})</td><td className="px-2 py-1.5 text-right lx-num" data-testid="cpo-total">{fmtNum(poTotal)}</td><td /></tr></tfoot>
              </table>
            </div>
          </div>

          <div><Label className="lx-label">Notes</Label><Textarea className="mt-1" rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
    </>
  );

  const cpoPreview = (
      <DocumentPreview
            open={poPreview} onOpenChange={setPoPreview}
            title="Client PO"
            onConfirm={async () => { if (await save()) setPoPreview(false); }}
            meta={[
              ["Client PO No", form.client_po_no],
              ["Client", (clients.find((c) => c.id === form.client_id) || {}).name],
              ["RFQ No", form.rfq_no],
              ["PO Date", form.po_date ? fmtDate(form.po_date) : "—"],
              ["Currency", form.currency || "TZS"],
              ["FX Rate", form.fx_rate],
            ]}
            columns={[
              { key: "item", label: "Item" },
              { key: "qty", label: "Qty", align: "right" },
              { key: "price", label: "Unit Price", align: "right" },
              { key: "amt", label: "Amount", align: "right" },
            ]}
            rows={(form.line_items || []).map((l) => ({
              item: l.description || l.sku || "—",
              qty: fmtNum(l.qty), price: fmtNum(l.unit_price),
              amt: fmtNum(Number(l.qty || 0) * Number(l.unit_price || 0)),
            }))}
            totals={[["Total Selling Price", `${form.currency || "TZS"} ${fmtNum(poTotal)}`, true]]}
          />
  );

  if (routeId === "new") {
    return (
      <Layout>
        <PageHeader title="New Client PO" subtitle="Register a client purchase order"
          actions={<Button variant="outline" data-testid="back-to-client-pos-new" onClick={() => navigate("/client-pos")}><ArrowLeft size={16} className="mr-1" />Back to list</Button>} />
        <div className="p-8">
          <div className="mx-auto max-w-6xl rounded-lg border border-gray-200 bg-white p-6 shadow-sm" data-testid="cpo-create-page">
            {cpoFormInner}
            <div className="mt-4 flex justify-end gap-2 border-t border-gray-100 pt-4">
              <Button variant="outline" onClick={() => navigate("/client-pos")}>Cancel</Button>
              <Button variant="outline" data-testid="preview-client-po" onClick={() => setPoPreview(true)}>Preview</Button>
              <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="save-client-po" onClick={async () => { if (await save()) navigate("/client-pos"); }}>Save</Button>
            </div>
          </div>
        </div>
        {cpoPreview}
      </Layout>
    );
  }

  if (routeId) {
    return (
      <Layout>
        <PageHeader
          title={detail ? `Client PO · ${detail.po.client_po_no}` : "Client PO"}
          subtitle="Deliver & invoice across supplier POs and stock, or fulfil from stock"
          actions={<Button variant="outline" data-testid="back-to-client-pos" onClick={() => navigate("/client-pos")}><ArrowLeft size={16} className="mr-1" />Back to list</Button>}
        />
        <div className="p-8">
          {notFound ? (
            <div className="mx-auto max-w-lg rounded-sm border border-gray-200 bg-white p-8 text-center" data-testid="cpo-detail-notfound">
              <div className="text-sm text-gray-500">This Client PO could not be found. It may have been deleted.</div>
              <Button className="mt-4" variant="outline" onClick={() => navigate("/client-pos")}><ArrowLeft size={16} className="mr-1" />Back to list</Button>
            </div>
          ) : !detail ? (
            <div className="text-sm text-gray-400" data-testid="cpo-detail-loading">Loading…</div>
          ) : (
            <div className="mx-auto max-w-5xl space-y-5" data-testid="cpo-detail-page">
              <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-gray-200 bg-white px-5 py-4 shadow-sm">
                <div>
                  <div className="flex items-center gap-2 font-display text-base font-semibold text-gray-800">
                    <Package size={20} className="text-[#1B4383]" />
                    Client PO · {detail.po.client_po_no}
                  </div>
                  <div className="mt-1 text-sm text-gray-600">{detail.po.client_name} · {detail.po.currency}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <StatusPill status={detail.fulfillment_status} />
                  <span className="text-xs text-gray-500" data-testid="cpo-last-updated">
                    {(() => {
                      const po = detail.po;
                      const at = po.updated_at || po.created_at;
                      const who = po.updated_by || po.created_by;
                      if (!at && !who) return "—";
                      return `${po.updated_at ? "Updated" : "Created"} ${at ? fmtDate(at) : ""}${who ? ` · ${who}` : ""}`;
                    })()}
                  </span>
                </div>
              </div>
              <div className="flex justify-end"><RequestAdvanceButton type="Advance Receipt" partyId={detail.po.client_id} poId={detail.po.id} poNo={detail.po.client_po_no} currency={detail.po.currency || "TZS"} testid="cpo-request-deposit" /></div>

              <Tabs defaultValue="deliver">
                <TabsList data-testid="cpo-detail-tabs">
                  <TabsTrigger value="deliver" data-testid="cpo-tab-deliver">Deliver &amp; Invoice</TabsTrigger>
                  <TabsTrigger value="fulfil" data-testid="cpo-tab-fulfil">Fulfil from Stock</TabsTrigger>
                  <TabsTrigger value="linked" data-testid="cpo-tab-linked">Linked Records</TabsTrigger>
                </TabsList>

                <TabsContent value="deliver" className="mt-5"><DeliverInvoice cpoId={detail.po.id} /></TabsContent>

                <TabsContent value="fulfil" className="mt-5">
                  <SectionCard title="Fulfil Lines" testid="cpo-card-fulfil">
                    <div className="overflow-x-auto rounded-sm border border-gray-200">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200 text-gray-400">
                          <th className="px-2 py-1.5 text-left">Item</th>
                          <th className="px-2 py-1.5 text-right">Ordered</th>
                          <th className="px-2 py-1.5 text-right">Done</th>
                          <th className="px-2 py-1.5 text-right">Remaining</th>
                          <th className="px-2 py-1.5 text-right">In Stock</th>
                          <th className="px-2 py-1.5 text-right">Fulfil Qty</th>
                          <th className="px-2 py-1.5 text-right">Price</th>
                        </tr></thead>
                        <tbody>
                          {(detail.lines || []).map((l) => {
                            const rem = l.qty_remaining;
                            const stock = l.available_stock;
                            const priceVal = ful[l.line_index]?.unit_price ?? l.unit_price;
                            const overridden = Number(priceVal) !== Number(l.unit_price);
                            const lowStock = stock != null && stock < rem;
                            return (
                              <tr key={l.line_index} className="border-b border-gray-50" data-testid={`ful-line-${l.line_index}`}>
                                <td className="px-2 py-1.5">
                                  <div className="font-medium">{l.sku || "—"}</div>
                                  <div className="text-xs text-gray-500">{l.description}</div>
                                </td>
                                <td className="px-2 py-1.5 text-right lx-num">{fmtNum(l.qty_ordered)}</td>
                                <td className="px-2 py-1.5 text-right lx-num">{fmtNum(l.qty_fulfilled)}</td>
                                <td className="px-2 py-1.5 text-right lx-num font-medium">{fmtNum(rem)}</td>
                                <td className={`px-2 py-1.5 text-right lx-num ${lowStock ? "text-red-600 font-semibold" : ""}`} data-testid={`ful-stock-${l.line_index}`}>
                                  {stock == null ? "—" : fmtNum(stock)}
                                </td>
                                <td className="px-2 py-1.5 text-right" style={{ width: 96 }}>
                                  <Input type="number" className="h-8 text-right" data-testid={`ful-qty-${l.line_index}`}
                                    disabled={rem <= 0}
                                    max={rem} min={0}
                                    value={ful[l.line_index]?.qty ?? ""}
                                    onChange={(e) => {
                                      const cap = Math.min(rem, stock == null ? Infinity : stock);
                                      let v = e.target.value;
                                      if (v !== "" && Number(v) > cap) v = String(cap);
                                      setFulLine(l.line_index, { qty: v });
                                    }} />
                                </td>
                                <td className="px-2 py-1.5 text-right" style={{ width: 140 }}>
                                  <Input type="number" className={`h-8 text-right ${overridden ? "border-amber-400 bg-amber-50" : ""}`} data-testid={`ful-price-${l.line_index}`}
                                    value={priceVal}
                                    onChange={(e) => setFulLine(l.line_index, { unit_price: e.target.value })} />
                                  {overridden && <div className="text-[10px] text-amber-600" data-testid={`ful-price-flag-${l.line_index}`}>≠ PO {fmtNum(l.unit_price)}</div>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="mt-4 flex items-center justify-end gap-2">
                      <Button variant="outline" onClick={() => navigate("/client-pos")}>Close</Button>
                      <Button variant="outline" data-testid="add-to-open-po-btn" onClick={addToOpenPo}>Add to existing open PO</Button>
                      <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="do-fulfill-btn" disabled={fulfilling} onClick={doFulfill}>
                        {fulfilling ? <CircleNotch size={16} className="mr-1 animate-spin" /> : <ArrowRight size={16} className="mr-1" />}
                        Fulfill from Stock
                      </Button>
                    </div>
                  </SectionCard>
                </TabsContent>

                <TabsContent value="linked" className="mt-5 space-y-5">
                  <SectionCard title="Linked Fulfillments" testid="cpo-card-linked-txns">
                    {(detail.transactions || []).length === 0 ? (
                      <div className="rounded-sm border border-dashed border-gray-200 px-3 py-4 text-center text-xs text-gray-400" data-testid="cpo-no-linked-txns">No fulfillments yet</div>
                    ) : (
                      <div className="overflow-hidden rounded-sm border border-gray-200">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200 text-gray-400">
                            <th className="px-2 py-1.5 text-left">Somcore PO</th>
                            <th className="px-2 py-1.5 text-left">Status</th>
                            <th className="px-2 py-1.5 text-right">Value</th>
                            <th className="px-2 py-1.5"></th>
                          </tr></thead>
                          <tbody>
                            {detail.transactions.map((t) => (
                              <tr key={t.id} className="border-b border-gray-50" data-testid={`cpo-linked-txn-${t.id}`}>
                                <td className="px-2 py-1.5 font-mono-num font-medium">{t.lx_po_no}</td>
                                <td className="px-2 py-1.5 text-xs">{t.status}</td>
                                <td className="px-2 py-1.5 text-right lx-num">{curSym(t.currency)} {fmtNum(t.total_amount)}</td>
                                <td className="px-2 py-1.5 text-right">
                                  <button className="text-xs font-semibold text-[#1B4383] hover:underline" data-testid={`open-linked-txn-${t.id}`} onClick={() => navigate(`/transactions/${t.id}`)}>Open</button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </SectionCard>

                  {(detail.client_invoices || []).length > 0 && (
                    <SectionCard title="Linked Client Invoices" testid="cpo-card-linked-inv">
                      <div className="overflow-hidden rounded-sm border border-gray-200">
                        <table className="w-full text-sm">
                          <tbody>
                            {detail.client_invoices.map((inv) => (
                              <tr key={inv.id} className="border-b border-gray-50" data-testid={`cpo-linked-inv-${inv.id}`}>
                                <td className="px-2 py-1.5 font-mono-num font-medium">{inv.invoice_no || inv.doc_no}</td>
                                <td className="px-2 py-1.5 text-right lx-num">{curSym(inv.currency)} {fmtNum(inv.total_amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </SectionCard>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader
        title="Client POs"
        subtitle="Register client purchase orders with line pricing — the source of truth for unbilled-revenue accrual"
        actions={<Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="new-client-po-btn" onClick={openNew}><Plus size={16} className="mr-1" />New Client PO</Button>}
      />
      <div className="p-8">
        <KpiStrip cols={3}>
          <KpiCard icon={Coins} label="Open PO Value" value={`TSh ${fmtNum(cpoOpenValue)}`} sub={`${cpoOpen.length} open`} testid="kpi-cpo-open-value" />
          <KpiCard icon={Package} label="Awaiting Fulfilment" value={`TSh ${fmtNum(cpoAwaitFulValue)}`} sub={`${cpoAwaitFul.length} POs`} tone="info" testid="kpi-cpo-awaiting-ful" />
          <KpiCard icon={Receipt} label="Awaiting Invoice" value={`TSh ${fmtNum(cpoAwaitInvValue)}`} sub={`${cpoAwaitInv.length} unbilled`} tone="warning" testid="kpi-cpo-awaiting-inv" />
        </KpiStrip>
        {rows.length === 0 ? (
          <EmptyState icon={Receipt} testid="client-pos-empty" title="No client POs yet" message="Register a client purchase order — the source of truth for unbilled-revenue accrual." />
        ) : (
        <>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-2.5 top-2.5 text-gray-400" />
            <Input data-testid="cpo-search" placeholder="Search PO / client / RFQ…" className="w-64 pl-8" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <MultiSelectFilter label="Fulfillment" options={CPO_STATUSES} selected={statusSel} onChange={setStatusSel} testid="cpo-filter-status" width="w-56" />
          <span className="text-xs text-gray-400" data-testid="cpo-filter-count">{filtered.length} of {rows.length}</span>
          <Button variant="outline" className="ml-auto" data-testid="cpo-export-btn" onClick={() => exportToCsv("client-pos", filtered, [
            { key: "client_po_no", label: "Client PO No" },
            { key: "lynx_po_nos", label: "Somcore POs", format: (v) => (v || []).join(" / ") },
            { key: "client_name", label: "Client" },
            { key: "rfq_no", label: "RFQ No" },
            { key: "po_date", label: "PO Date", format: (v) => (v ? fmtDate(v) : "") },
            { key: "currency", label: "Currency" },
            { key: "fx_rate", label: "FX Rate" },
            { key: "fulfillment_status", label: "Fulfillment", format: (v) => v || "Not Started" },
            { key: "line_items", label: "Lines", format: (v) => (v || []).length },
            { key: "line_items", label: "PO Value (excl)", format: (v) => (v || []).reduce((s, l) => s + (l.exclusive_amount || 0), 0) },
          ])}><DownloadSimple size={16} className="mr-1" />Export</Button>
        </div>
        <div className="max-h-[calc(100vh-16rem)] overflow-auto rounded-sm border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50"><tr className="lx-label border-b border-gray-200">
              <th className="px-4 py-2 text-left">Client PO No</th><th className="px-4 py-2 text-left">Somcore PO</th><th className="px-4 py-2 text-left">Client</th>
              <th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-left">Currency</th>
              <th className="px-4 py-2 text-left">Fulfillment</th>
              <th className="px-4 py-2 text-right">Lines</th><th className="px-4 py-2 text-right">PO Value</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {filtered.length === 0 && <TableEmpty colSpan={9} title="No matches" message="No client POs match this filter." testid="cpo-filter-empty" />}
              {filtered.map((p) => {
                const val = (p.line_items || []).reduce((s, l) => s + (l.exclusive_amount || 0), 0);
                return (
                  <tr key={p.id} data-testid={`client-po-row-${p.id}`} className="cursor-pointer border-b border-gray-100 hover:bg-gray-50" onClick={() => navigate(`/client-pos/${p.id}`)}>
                    <td className="px-4 py-2">
                      <button className="font-mono-num font-medium text-[#1B4383] hover:underline" data-testid={`open-client-po-${p.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/client-pos/${p.id}`); }}>{p.client_po_no}</button>
                    </td>
                    <td className="px-4 py-2" data-testid={`cpo-lynx-po-${p.id}`}>{p.lynx_po_nos && p.lynx_po_nos.length ? <span className="font-mono-num">{p.lynx_po_nos.join(", ")}</span> : <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 text-xs text-gray-400">Not Linked</span>}</td>
                    <td className="px-4 py-2">{p.client_name || "—"}</td>
                    <td className="px-4 py-2 lx-num">{fmtDate(p.po_date)}</td>
                    <td className="px-4 py-2">{p.currency}{p.fx_rate && p.fx_rate !== 1 ? ` @${p.fx_rate}` : ""}</td>
                    <td className="px-4 py-2" data-testid={`cpo-status-${p.id}`}><StatusPill status={p.fulfillment_status} /></td>
                    <td className="px-4 py-2 text-right lx-num">{(p.line_items || []).length}</td>
                    <td className="px-4 py-2 text-right lx-num">{fmtNum(val)}</td>
                    <td className="px-4 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                      <button className="text-xs font-semibold text-[#1B4383] hover:underline" data-testid={`fulfill-client-po-${p.id}`} onClick={() => navigate(`/client-pos/${p.id}`)}>Fulfill</button>
                      <button className="ml-3 text-xs text-gray-500 hover:text-[#1B4383]" data-testid={`edit-client-po-${p.id}`} onClick={() => openEdit(p)}>Edit</button>
                      <button className="ml-3 text-xs text-gray-400 hover:text-red-500" data-testid={`delete-client-po-${p.id}`} onClick={() => del(p)}>Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="client-po-dialog">
          <DialogHeader><DialogTitle className="font-display">{form.id ? "Edit" : "New"} Client PO</DialogTitle></DialogHeader>
          {cpoFormInner}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="outline" data-testid="preview-client-po" onClick={() => setPoPreview(true)}>Preview</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="save-client-po" onClick={save}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {cpoPreview}
    </Layout>
  );
}
